/* global.js — minimal, dependency-free, deferred.
   Performance rule for this theme: no framework, no jQuery. Prefer native HTML
   (<details>, <dialog>) + small Web Components per section, loaded only where used. */
(function () {
  'use strict';

  // Mark when fonts are ready so we can avoid layout shift hooks if needed.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      document.documentElement.classList.add('fonts-loaded');
    });
  }

  /* Example shared Web Component: <details>-free disclosure toggle.
     Sections can reuse <button is-toggle aria-controls="ID"> to show/hide. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-toggle]');
    if (!trigger) return;
    var target = document.getElementById(trigger.getAttribute('data-toggle'));
    if (!target) return;
    var open = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!open));
    target.hidden = open;
  });

  /* Favorites — a lightweight client-side wishlist kept in localStorage. Any
     [data-favorite][data-favorite-id] button toggles membership and reflects state
     (aria-pressed + label). No backend; the header Favorites link is the landing page. */
  var FAV_KEY = 'pu:favorites';
  function readFavs() {
    try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); } catch (e) { return []; }
  }
  function writeFavs(list) {
    try { localStorage.setItem(FAV_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function paintFav(btn, active) {
    btn.setAttribute('aria-pressed', String(active));
    var label = btn.querySelector('[data-favorite-label]');
    if (label) label.textContent = active ? 'In Favorites' : 'Add to Favorites';
  }
  function syncFavs() {
    var favs = readFavs();
    document.querySelectorAll('[data-favorite][data-favorite-id]').forEach(function (btn) {
      paintFav(btn, favs.indexOf(btn.getAttribute('data-favorite-id')) !== -1);
    });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-favorite][data-favorite-id]');
    if (!btn) return;
    e.preventDefault();
    var id = btn.getAttribute('data-favorite-id');
    var favs = readFavs();
    var i = favs.indexOf(id);
    if (i === -1) favs.push(id); else favs.splice(i, 1);
    writeFavs(favs);
    paintFav(btn, i === -1);
  });
  if (document.readyState !== 'loading') syncFavs();
  else document.addEventListener('DOMContentLoaded', syncFavs);

  /* Shared image lightbox — any element with [data-lightbox-image] (and optional
     [data-lightbox-alt]) opens the enlarged image in the layout's <dialog data-img-lightbox>.
     (The product gallery has its own lightbox in product.js via data-zoom-src, kept separate.) */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-lightbox-image]');
    if (!trigger) return;
    var dialog = document.querySelector('[data-img-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    e.preventDefault();
    var img = dialog.querySelector('[data-img-lightbox-img]');
    var nextSrc = trigger.getAttribute('data-lightbox-image');
    // Avoid flashing the previously-viewed image while the new (large) one loads.
    if (img.getAttribute('src') !== nextSrc) {
      img.style.opacity = '0';
      img.onload = function () { img.style.opacity = '1'; };
      img.src = nextSrc;
    } else {
      img.style.opacity = '1';
    }
    img.alt = trigger.getAttribute('data-lightbox-alt') || '';
    if (!dialog.open) dialog.showModal();
  });
  document.addEventListener('click', function (e) {
    var dialog = e.target.closest('[data-img-lightbox]');
    if (!dialog) return;
    // Close on the close button or on backdrop (click outside the figure).
    if (e.target.closest('[data-img-lightbox-close]') || !e.target.closest('.img-lightbox__figure')) {
      dialog.close();
    }
  });

  /* Product-card hover video — when a card's second media is a hosted video, play it
     (muted) while hovering the card and reset it on leave, mirroring the image-swap
     hover. Delegated + pointerover/out (which bubble) so it covers cards added later
     (facet reloads, quick view, cart drawer recs). The gate is the CSS hover reveal
     query, (hover: hover) and (prefers-reduced-motion: no-preference), and .matches
     is read inside the handler. A phone or a reduced-motion desktop never fetches
     or plays the file. Sources and the poster stay on data-src and data-poster
     until the first pointerover. data-card-video-pending holds the photo up until
     a frame or a loaded poster exists. A source error does not bubble, so the
     error listener is capture. The 8s timer only gives up waiting. It leaves
     the frame listeners in place, and the poster onload checks again, so a
     late file or poster still clears the flag. The photo stays until then.
     Nothing asks for the file again. */
  var cardVideoMedia = window.matchMedia
    ? window.matchMedia('(hover: hover) and (prefers-reduced-motion: no-preference)')
    : null;
  function cardVideoHoverOk() {
    return !!(cardVideoMedia && cardVideoMedia.matches);
  }
  document.addEventListener('pointerover', function (e) {
    if (!cardVideoHoverOk()) return;
    var wrap = e.target.closest && e.target.closest('.product-card__media-wrap');
    if (!wrap) return;
    var vid = wrap.querySelector('video[data-card-video]');
    if (!vid) return;
    if (vid.getAttribute('data-card-video-armed') !== 'true') {
      vid.querySelectorAll('source[data-src]').forEach(function (source) {
        var src = source.getAttribute('data-src');
        if (src) source.setAttribute('src', src);
      });
      var poster = vid.getAttribute('data-poster');
      if (poster) vid.setAttribute('poster', poster);
      vid.setAttribute('data-card-video-armed', 'true');
      wrap.setAttribute('data-card-video-pending', '');
      var posterReady = false;
      var posterProbe = null;
      var pendingTimer;
      var detach = function () {
        window.clearTimeout(pendingTimer);
        vid.removeEventListener('loadeddata', onFrame);
        vid.removeEventListener('playing', onFrame);
        vid.removeEventListener('error', onError, true);
      };
      var canReveal = function () {
        if (vid.readyState >= 2) return true;
        if (posterReady) return true;
        return !!(posterProbe && posterProbe.complete && posterProbe.naturalWidth > 0);
      };
      var revealIfReady = function () {
        if (!canReveal()) return;
        detach();
        wrap.removeAttribute('data-card-video-pending');
      };
      var onFrame = revealIfReady;
      var onError = revealIfReady;
      var onTimeout = function () {
        window.clearTimeout(pendingTimer);
        // Leave the frame listeners. A late file or poster still clears the flag.
        if (canReveal()) revealIfReady();
      };
      vid.addEventListener('loadeddata', onFrame);
      vid.addEventListener('playing', onFrame);
      // Source errors do not bubble. Capture on the video still hears them.
      vid.addEventListener('error', onError, true);
      pendingTimer = window.setTimeout(onTimeout, 8000);
      if (poster) {
        posterProbe = new Image();
        posterProbe.onload = function () {
          posterReady = true;
          revealIfReady();
        };
        posterProbe.src = poster;
      }
      vid.load();
    } else if (vid.readyState < 2 && (vid.error || vid.networkState === vid.NETWORK_NO_SOURCE)) {
      // The source already failed. play() would ask for the file again.
      return;
    }
    if (vid.paused) {
      var p = vid.play();
      if (p && p.catch) p.catch(function () {});
    }
  });
  document.addEventListener('pointerout', function (e) {
    if (!cardVideoHoverOk()) return;
    var wrap = e.target.closest && e.target.closest('.product-card__media-wrap');
    if (!wrap) return;
    // Ignore moves that stay inside the same card.
    if (e.relatedTarget && wrap.contains(e.relatedTarget)) return;
    var vid = wrap.querySelector('video[data-card-video]');
    if (vid) {
      vid.pause();
      try { vid.currentTime = 0; } catch (err) {}
    }
  });

  /* Product card and cart-rec clicks. One document listener, not per card.
     Quick view is a separate action from the image or title link. */
  if (!(window.__pragerAnalytics && window.__pragerAnalytics.cardBound)) {
    window.__pragerAnalytics = window.__pragerAnalytics || {};
    window.__pragerAnalytics.cardBound = true;

    function publishShopifyAnalytics(eventName, payload) {
      try {
        if (!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function')) return;
        window.Shopify.analytics.publish(eventName, payload);
      } catch (err) {}
    }
    window.puPublishAnalytics = publishShopifyAnalytics;

    function cardCents(card) {
      var n = parseInt(card.getAttribute('data-item-price'), 10);
      return isFinite(n) ? n / 100 : 0;
    }

    function listAttr(card, name) {
      var own = card.getAttribute(name);
      if (own) return own;
      var node = card.parentElement;
      while (node && node !== document.documentElement) {
        if (node.hasAttribute(name)) return node.getAttribute(name) || '';
        node = node.parentElement;
      }
      return '';
    }

    function cardIndex(card) {
      var root = card.closest('.tb-carousel__track, .tb-product-grid, .collection-grid, .menu-drawer__featured, .mega__features, .ps-products, [data-recently-viewed-grid], .cart-recs__track, .cart-recs__fallback, .cart-recs__results') || card.parentElement;
      if (!root) return 0;
      var cards = root.querySelectorAll('[data-item-id]');
      var index = 0;
      var i;
      for (i = 0; i < cards.length; i++) {
        if (cards[i].hidden) continue;
        if (cards[i] === card) return index;
        index += 1;
      }
      return 0;
    }

    function cardPayload(card) {
      if (!card) return null;
      var id = card.getAttribute('data-item-id');
      if (!id) return null;
      return {
        item_list_id: listAttr(card, 'data-item-list-id'),
        item_list_name: listAttr(card, 'data-item-list-name'),
        item: {
          item_id: id,
          item_name: card.getAttribute('data-item-name') || '',
          item_product_id: card.getAttribute('data-item-product-id') || '',
          item_variant: card.getAttribute('data-item-variant') || '',
          item_brand: card.getAttribute('data-item-brand') || '',
          item_category: card.getAttribute('data-item-category') || '',
          price: cardCents(card),
          index: cardIndex(card)
        }
      };
    }

    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var quick = target.closest('[data-quick-view]');
      if (quick) {
        var quickPayload = cardPayload(quick.closest('.product-card'));
        if (quickPayload) publishShopifyAnalytics('product_card_clicked', quickPayload);
        return;
      }
      var link = target.closest('a[href]');
      if (!link || link.closest('button, .product-card__form')) return;
      var card = link.closest('.product-card, .cart-rec');
      if (!card) return;
      if (card.classList.contains('product-card')) {
        if (link.classList.contains('product-card__swatch')) return;
        if (!link.classList.contains('product-card__media') && !link.classList.contains('product-card__title') && !link.classList.contains('btn--card')) return;
      } else if (!link.classList.contains('cart-rec__media') && !link.classList.contains('cart-rec__title') && !link.classList.contains('cart-rec__add')) {
        return;
      }
      var payload = cardPayload(card);
      if (payload) publishShopifyAnalytics('product_card_clicked', payload);
    });
  }
})();
